(() => {
  'use strict';

  const INSTALL_KEY = '__G21_DYNAMIC_PREVIEW_LINK_MANAGER__';
  if (window[INSTALL_KEY]) return;
  window[INSTALL_KEY] = true;

  const scriptElement = document.currentScript || document.getElementById('g21-link-yonetim-script');
  const ROOT_DOMAIN = String(scriptElement?.dataset.rootDomain || '').trim().toLowerCase();
  const RUNTIME_THEME_ID = String(scriptElement?.dataset.runtimeThemeId || '').trim();
  const RUNTIME_THEME_ROLE = String(scriptElement?.dataset.runtimeThemeRole || '').trim().toLowerCase();
  const PREVIEW_KEY = 'preview_theme_id';
  const PREVIEW_NOISE_KEYS = Object.freeze(['preview_key', '_fd', 'pb']);
  const currentPageUrl = new URL(window.location.href);
  const numericId = (value) => /^\d+$/.test(String(value || '').trim());
  const urlPreviewThemeId = currentPageUrl.searchParams
    .getAll(PREVIEW_KEY)
    .map((value) => String(value || '').trim())
    .find(numericId) || null;
  const runtimeThemeIsPreview =
    numericId(RUNTIME_THEME_ID) &&
    Boolean(RUNTIME_THEME_ROLE) &&
    RUNTIME_THEME_ROLE !== 'main';
  const previewThemeId = runtimeThemeIsPreview ? RUNTIME_THEME_ID : urlPreviewThemeId;
  const activationSource = runtimeThemeIsPreview
    ? (urlPreviewThemeId === RUNTIME_THEME_ID ? 'url-and-runtime-theme' : 'runtime-preview-theme')
    : (urlPreviewThemeId ? 'url' : 'inactive');

  const assignNative = (value) => window.location.assign(value);
  const replaceNative = (value) => window.location.replace(value);
  const nativeFormSubmit = HTMLFormElement.prototype.submit;

  const submitNative = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    nativeFormSubmit.call(form);
    return true;
  };

  const inactiveManager = Object.freeze({
    installed: true,
    active: false,
    previewThemeId: null,
    activationSource,
    runtimeThemeId: RUNTIME_THEME_ID || null,
    runtimeThemeRole: RUNTIME_THEME_ROLE || null,
    withPreview: (value) => value,
    updateAll: () => false,
    audit: () => ({
      active: false,
      previewThemeId: null,
      activationSource,
      runtimeThemeId: RUNTIME_THEME_ID || null,
      runtimeThemeRole: RUNTIME_THEME_ROLE || null
    }),
    submitForm: submitNative,
    navigate: (value, options = {}) => {
      if (options.replace) replaceNative(value);
      else assignNative(value);
    }
  });

  if (!ROOT_DOMAIN || !previewThemeId) {
    window.G21LinkManager = inactiveManager;
    return;
  }

  const URL_ATTRIBUTES = Object.freeze([
    'href',
    'formaction',
    'data-url',
    'data-href',
    'data-clean-language-url',
    'data-product-url',
    'data-connected-product-url'
  ]);

  const DATA_ATTRIBUTES = URL_ATTRIBUTES.filter(
    (attribute) => attribute !== 'href' && attribute !== 'formaction'
  );

  const LINK_SELECTOR = [
    'a[href]',
    'area[href]',
    '[formaction]',
    ...DATA_ATTRIBUTES.map((attribute) => `[${attribute}]`)
  ].join(',');

  const OBSERVED_ATTRIBUTES = Object.freeze([
    ...URL_ATTRIBUTES,
    'src',
    'action',
    'method'
  ]);

  const SYSTEM_PATH_PREFIXES = Object.freeze([
    '/admin',
    '/checkouts',
    '/checkout',
    '/challenge'
  ]);

  const observedRoots = new WeakSet();

  const isDocument = (value) => value instanceof Document;
  const isDocumentFragment = (value) => value instanceof DocumentFragment;
  const isShadowRoot = (value) => typeof ShadowRoot !== 'undefined' && value instanceof ShadowRoot;
  const isScannableRoot = (value) =>
    isDocument(value) || value instanceof Element || isDocumentFragment(value);

  const isSkippedRawValue = (value) => {
    const raw = String(value || '').trim();
    const lower = raw.toLowerCase();

    return (
      raw === '' ||
      raw.startsWith('#') ||
      lower.startsWith('mailto:') ||
      lower.startsWith('tel:') ||
      lower.startsWith('sms:') ||
      lower.startsWith('javascript:') ||
      lower.startsWith('data:') ||
      lower.startsWith('blob:')
    );
  };

  const isInternalUrl = (url) => {
    if (!['http:', 'https:'].includes(url.protocol)) return false;
    const hostname = url.hostname.toLowerCase();
    return hostname === ROOT_DOMAIN || hostname.endsWith(`.${ROOT_DOMAIN}`);
  };

  const isExcludedService = (url) => {
    const hostname = url.hostname.toLowerCase();
    return hostname === 'wa.me' || hostname === 'api.whatsapp.com';
  };

  const isSystemPath = (url) => {
    const path = url.pathname.toLowerCase();
    return SYSTEM_PATH_PREFIXES.some(
      (prefix) => path === prefix || path.startsWith(`${prefix}/`)
    );
  };

  const cleanPreviewNoise = (url) => {
    PREVIEW_NOISE_KEYS.forEach((key) => url.searchParams.delete(key));
  };

  const serializeUrl = (url, rawValue) => {
    const raw = String(rawValue || '').trim();
    const isRelativeSameOrigin =
      url.origin === window.location.origin &&
      !/^[a-z][a-z0-9+.-]*:/i.test(raw) &&
      !raw.startsWith('//');

    return isRelativeSameOrigin
      ? `${url.pathname}${url.search}${url.hash}`
      : url.href;
  };

  const withPreview = (value) => {
    if (isSkippedRawValue(value)) return value;

    try {
      const raw = String(value).trim();
      const url = new URL(raw, window.location.href);

      if (!isInternalUrl(url) || isExcludedService(url) || isSystemPath(url)) {
        return value;
      }

      cleanPreviewNoise(url);
      url.searchParams.delete(PREVIEW_KEY);
      url.searchParams.set(PREVIEW_KEY, previewThemeId);

      return serializeUrl(url, raw);
    } catch (_error) {
      return value;
    }
  };

  const updateAttribute = (element, attribute) => {
    if (!(element instanceof Element) || !element.hasAttribute(attribute)) return false;

    const currentValue = element.getAttribute(attribute);
    const updatedValue = withPreview(currentValue);

    if (typeof updatedValue === 'string' && updatedValue !== currentValue) {
      element.setAttribute(attribute, updatedValue);
      return true;
    }

    return false;
  };

  const updateLinkElement = (element) => {
    if (!(element instanceof Element) || !element.matches(LINK_SELECTOR)) return false;

    let changed = false;
    URL_ATTRIBUTES.forEach((attribute) => {
      changed = updateAttribute(element, attribute) || changed;
    });
    return changed;
  };

  const ensureSingleHiddenField = (form, name, value) => {
    const fields = Array.from(form.querySelectorAll(`input[name="${name}"]`));
    let field = fields.shift();
    let changed = false;

    fields.forEach((duplicate) => {
      duplicate.remove();
      changed = true;
    });

    if (!(field instanceof HTMLInputElement)) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = name;
      field.dataset.g21Generated = 'true';
      form.appendChild(field);
      changed = true;
    }

    if (field.type !== 'hidden') {
      field.type = 'hidden';
      changed = true;
    }

    if (field.value !== value) {
      field.value = value;
      changed = true;
    }

    return changed;
  };

  const currentReturnTo = () => {
    const url = new URL(window.location.href);
    cleanPreviewNoise(url);
    url.searchParams.delete(PREVIEW_KEY);
    url.searchParams.set(PREVIEW_KEY, previewThemeId);
    return `${url.pathname}${url.search}${url.hash}`;
  };

  const updateLocalizationForm = (form, actionUrl) => {
    const path = actionUrl.pathname.toLowerCase();
    if (!(path === '/localization' || path.endsWith('/localization'))) return false;

    const existingReturnTo = form.querySelector('input[name="return_to"]')?.value;
    const returnTo = withPreview(existingReturnTo || currentReturnTo());
    return ensureSingleHiddenField(form, 'return_to', returnTo);
  };

  const updateForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;

    try {
      const actionValue = form.getAttribute('action') || window.location.href;
      const actionUrl = new URL(actionValue, window.location.href);

      if (!isInternalUrl(actionUrl) || isSystemPath(actionUrl)) return false;

      if (updateLocalizationForm(form, actionUrl)) return true;

      const method = String(form.getAttribute('method') || 'get').trim().toLowerCase();
      if (method !== 'get') return false;

      return ensureSingleHiddenField(form, PREVIEW_KEY, previewThemeId);
    } catch (_error) {
      return false;
    }
  };

  const submitForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    updateForm(form);
    return submitNative(form);
  };

  const updateIframe = (iframe) => {
    if (!(iframe instanceof HTMLIFrameElement) || !iframe.hasAttribute('src')) return false;

    const currentValue = iframe.getAttribute('src');
    const updatedValue = withPreview(currentValue);

    if (typeof updatedValue === 'string' && updatedValue !== currentValue) {
      iframe.setAttribute('src', updatedValue);
      return true;
    }

    return false;
  };

  const scanOpenShadowRoots = (root) => {
    if (root instanceof Element && root.shadowRoot) observeRoot(root.shadowRoot);

    root.querySelectorAll?.('*').forEach((element) => {
      if (element.shadowRoot) observeRoot(element.shadowRoot);
    });
  };

  const updateAll = (root = document) => {
    if (!isScannableRoot(root)) return false;

    let changed = false;

    if (root instanceof Element) {
      changed = updateLinkElement(root) || changed;
      changed = updateForm(root) || changed;
      changed = updateIframe(root) || changed;
    }

    root.querySelectorAll?.(LINK_SELECTOR).forEach((element) => {
      changed = updateLinkElement(element) || changed;
    });

    root.querySelectorAll?.('form').forEach((form) => {
      changed = updateForm(form) || changed;
    });

    root.querySelectorAll?.('iframe[src]').forEach((iframe) => {
      changed = updateIframe(iframe) || changed;
    });

    scanOpenShadowRoots(root);
    return changed;
  };

  const observer = new MutationObserver((records) => {
    records.forEach((record) => {
      if (record.type === 'attributes' && record.target instanceof Element) {
        updateLinkElement(record.target);
        updateForm(record.target);
        updateIframe(record.target);
        return;
      }

      record.addedNodes.forEach((node) => {
        if (node instanceof Element || isDocumentFragment(node)) {
          updateAll(node);
        }
      });
    });
  });

  function observeRoot(root) {
    if (!(isDocument(root) || isShadowRoot(root)) || observedRoots.has(root)) return;

    observedRoots.add(root);
    updateAll(root);

    const target = isDocument(root) ? root.documentElement : root;
    if (!target) return;

    observer.observe(target, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: OBSERVED_ATTRIBUTES
    });
  }

  const nativeAttachShadow = Element.prototype.attachShadow;
  if (typeof nativeAttachShadow === 'function' && !nativeAttachShadow.__g21PreviewWrapped) {
    const wrappedAttachShadow = function (init) {
      const shadowRoot = nativeAttachShadow.call(this, init);
      if (init?.mode === 'open') queueMicrotask(() => observeRoot(shadowRoot));
      return shadowRoot;
    };

    Object.defineProperty(wrappedAttachShadow, '__g21PreviewWrapped', { value: true });

    try {
      Element.prototype.attachShadow = wrappedAttachShadow;
    } catch (_error) {
      // Existing open Shadow DOM roots continue to be scanned.
    }
  }

  const wrapHistoryMethod = (methodName) => {
    const nativeMethod = history[methodName];
    if (typeof nativeMethod !== 'function') return;

    history[methodName] = function (state, unused, url) {
      const nextUrl = url == null ? url : withPreview(url instanceof URL ? url.href : String(url));
      return nativeMethod.call(history, state, unused, nextUrl);
    };
  };

  wrapHistoryMethod('pushState');
  wrapHistoryMethod('replaceState');

  if (!nativeFormSubmit.__g21PreviewWrapped) {
    const wrappedFormSubmit = function () {
      updateForm(this);
      return nativeFormSubmit.call(this);
    };
    Object.defineProperty(wrappedFormSubmit, '__g21PreviewWrapped', { value: true });
    try {
      HTMLFormElement.prototype.submit = wrappedFormSubmit;
    } catch (_error) {
      // Capture submit handling and submitForm() remain available.
    }
  }

  const nativeWindowOpen = window.open;
  if (typeof nativeWindowOpen === 'function') {
    window.open = function (url, target, features) {
      const rawUrl = url instanceof URL ? url.href : url;
      const nextUrl = typeof rawUrl === 'string' ? withPreview(rawUrl) : rawUrl;
      return nativeWindowOpen.call(window, nextUrl, target, features);
    };
  }

  const navigate = (value, options = {}) => {
    const rawValue = value instanceof URL ? value.href : String(value);
    const nextUrl = withPreview(rawValue);

    if (options.replace) replaceNative(nextUrl);
    else assignNative(nextUrl);
  };

  const audit = (root = document) => {
    const result = {
      active: true,
      previewThemeId,
      activationSource,
      runtimeThemeId: RUNTIME_THEME_ID || null,
      runtimeThemeRole: RUNTIME_THEME_ROLE || null,
      inspected: 0,
      wrongThemeId: 0,
      duplicateThemeId: 0,
      missingThemeId: 0,
      previewNoise: 0
    };

    if (!isScannableRoot(root)) return result;

    root.querySelectorAll?.(LINK_SELECTOR).forEach((element) => {
      URL_ATTRIBUTES.forEach((attribute) => {
        if (!element.hasAttribute(attribute)) return;

        const raw = element.getAttribute(attribute);
        if (isSkippedRawValue(raw)) return;

        try {
          const url = new URL(raw, window.location.href);
          if (!isInternalUrl(url) || isSystemPath(url)) return;

          result.inspected += 1;
          const values = url.searchParams.getAll(PREVIEW_KEY);

          if (values.length === 0) result.missingThemeId += 1;
          if (values.length > 1) result.duplicateThemeId += 1;
          if (values.some((value) => value !== previewThemeId)) result.wrongThemeId += 1;
          if (PREVIEW_NOISE_KEYS.some((key) => url.searchParams.has(key))) result.previewNoise += 1;
        } catch (_error) {
          // Invalid URL values are excluded from the audit.
        }
      });
    });

    return result;
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const directTarget = event.target.closest(LINK_SELECTOR);
    if (directTarget) updateLinkElement(directTarget);

    const productCard = event.target.closest('product-card');
    if (productCard) updateAll(productCard);
  }, true);

  document.addEventListener('submit', (event) => {
    if (event.target instanceof HTMLFormElement) updateForm(event.target);
  }, true);

  [
    'shopify:section:load',
    'shopify:section:select',
    'shopify:block:select'
  ].forEach((eventName) => {
    document.addEventListener(eventName, (event) => updateAll(event.target));
  });

  window.addEventListener('pageshow', () => updateAll(document));

  observeRoot(document);
  document.documentElement.dataset.g21PreviewThemeId = previewThemeId;
  document.documentElement.dataset.g21PreviewThemeSource = activationSource;

  window.G21LinkManager = Object.freeze({
    installed: true,
    active: true,
    previewThemeId,
    activationSource,
    runtimeThemeId: RUNTIME_THEME_ID || null,
    runtimeThemeRole: RUNTIME_THEME_ROLE || null,
    withPreview,
    updateAll,
    audit,
    submitForm,
    navigate
  });
})();
